import { deepEqual } from '@tunasong/models'
import type { Comment, CoreElement, Persisted } from '@tunasong/schemas'
import { isEntity, isPersisted } from '@tunasong/schemas'

/**
 * Decide whether a Slate `onChange` payload from the readOnly `<ChatEditor>`
 * represents a real content edit that warrants a PATCH to the entity.
 *
 * GH #1280: Plate's `onValueChange` fires on the editor's initial value
 * normalization *and* every time the bound entity reference changes (e.g.
 * an optimistic copy is replaced by the synced version via
 * `state.entities.receivedEntities`). The old `handleCommentUpdate` wired
 * that callback straight to `updateEntity`, which produced:
 *
 *   - phantom `PATCH /entities/<id>` for every rendered comment on mount
 *   - a 404 on the just-POSTed comment, because PATCH raced the create
 *     and reached the server before the row was persisted
 *
 * Gate the PATCH on a deep equality compare of `children` against the
 * stored original. The Slate document is canonical JSON so deepEqual is
 * structural and reference-stable.
 *
 * The `isPersisted` and `id` guards are defensive — `onChange` from
 * `ElementEditor` already preserves the entity-shape spread, but we never
 * want to PATCH the wrong row if anything upstream changes.
 */
export function shouldPatchComment(
  original: Persisted<Comment>,
  candidate: CoreElement
): candidate is Persisted<Comment> {
  if (!(isPersisted(candidate) && isEntity(candidate))) {
    return false
  }
  if (candidate.id !== original.id) {
    return false
  }
  return !deepEqual(original.children, candidate.children)
}
